import { DraggableProvided } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import CircleNumber from 'packages/kakapo-ui-kit/components/common/CircleNumber';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import InputResizable from 'packages/kakapo-ui-kit/components/forms/controls/InputResizable';
import FeedbackQuestionTypeSelector from './FeedbackQuestionTypeSelector';
import {
	ComposableListItem,
	ComposableListItemControls,
	ComposableListItemControlsButton,
} from 'packages/kakapo-ui-kit/components/lists/ComposableList';
import CloseIcon from 'packages/kakapo-ui-kit/assets/icons/CloseIcon';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';

const Outline = styled.div`
	box-shadow: 0 1px 6px 0 rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);
	background-color: ${(props) => props.theme.colors.white};
	border-radius: 6px;
	margin-bottom: 10px;
`;

const Input = styled(InputResizable)`
	width: 100%;
	resize: none;
	font-size: 16px;
	color: ${(props) => props.theme.colors.slateBlue};
	border: 0;
	outline: 0;
	padding: 3px 0px;

	&:focus {
		border: 0;
		outline: 0;
	}
`;

export default function FeedbackComposerQuestion(props: {
	provided: DraggableProvided;
	selected: boolean;
	index: number;
	question: graphql.FeedbackRequestQuestionInput;
	onFocus: () => void;
	onChange: (question: graphql.FeedbackRequestQuestionInput) => void;
	onRemove: () => void;
}) {
	const { t } = useTranslation();

	const controls = (
		<ComposableListItemControls>
			<Flex flexDirection="row">
				<ComposableListItemControlsButton
					onMouseUp={(event) => {
						event.preventDefault();
						event.stopPropagation();
						props.onRemove();
					}}
				>
					<CloseIcon />
				</ComposableListItemControlsButton>
			</Flex>
		</ComposableListItemControls>
	);

	if (!props.selected) {
		return (
			<Box {...props.provided.dragHandleProps} onMouseUp={props.onFocus}>
				<ComposableListItem
					accessory={<CircleNumber color="slateBlue">{props.index + 1}</CircleNumber>}
					controls={controls}
				>
					<ParagraphSmall color="slateBlue">{props.question.text}</ParagraphSmall>
				</ComposableListItem>
			</Box>
		);
	}

	return (
		<Outline>
			<Flex alignItems="center" justifyContent="space-between" {...props.provided.dragHandleProps}>
				<Column>
					<FeedbackQuestionTypeSelector
						type={props.question.type}
						onChange={(type) => props.onChange({ ...props.question, type })}
					/>
				</Column>
				<Column px={3}>{controls}</Column>
			</Flex>

			<HorizontalRule />

			<Column px={3} pt={3} pb={1}>
				<Flex>
					<Column>
						<CircleNumber>{props.index + 1}</CircleNumber>
					</Column>

					<Column px={3} flex={1}>
						<Label title={t('feedback.composer.fillQueryLabel')} spaced={false} />
						<Input
							minRows={1}
							maxRows={15}
							lineHeight={24}
							autoFocus
							value={props.question.text}
							onChange={(event) =>
								props.onChange({ ...props.question, text: event.currentTarget.value })
							}
						/>
					</Column>
				</Flex>
			</Column>
		</Outline>
	);
}
